import { Info } from 'lucide-react';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { useGetActivityOptionsQuery } from '../../api/emissions';
import { EmissionScope, Scope1ActivityType } from '../../types/emissions';
import {
  FUEL_TYPE_LABELS,
  VEHICLE_TYPE_LABELS,
  GAS_TYPE_LABELS,
  PROCESS_TYPE_LABELS,
} from './types';

interface ImportGuidelinesProps {
  initiativeId: string;
  scope: EmissionScope;
  activityType?: Scope1ActivityType;
}

export const ImportGuidelines = ({ initiativeId, scope, activityType }: ImportGuidelinesProps) => {
  const { data: options } = useGetActivityOptionsQuery({ initiativeId }, { skip: !initiativeId });

  const fuelTypes = (activityType ? options?.fuelTypesByActivityType[activityType] : undefined)?.map((v) => FUEL_TYPE_LABELS[v] ?? v) ?? [];
  const vehicleTypes = options?.vehicleTypes?.map((v) => VEHICLE_TYPE_LABELS[v] ?? v) ?? [];
  const refrigerantTypes = options?.refrigerantTypes?.map((v) => GAS_TYPE_LABELS[v] ?? v) ?? [];
  const processTypes = options?.processTypes?.map((v) => PROCESS_TYPE_LABELS[v] ?? v) ?? [];

  const conditionalRequired: React.ReactNode[] = [];
  let validValues: { label: string; values: string[] } | null = null;

  if (activityType === Scope1ActivityType.StationaryCombustion) {
    conditionalRequired.push(<li key='fuel'>Fuel Type</li>);
    validValues = { label: 'Valid Fuel Types', values: fuelTypes };
  } else if (activityType === Scope1ActivityType.MobileCombustion) {
    conditionalRequired.push(<li key='vehicle'>Vehicle Type</li>);
    conditionalRequired.push(<li key='fuel'>Fuel Type (optional — improves accuracy)</li>);
    validValues = { label: 'Valid Vehicle Types', values: vehicleTypes };
  } else if (activityType === Scope1ActivityType.FugitiveEmissions) {
    conditionalRequired.push(<li key='refrigerant'>Gas / Refrigerant Type</li>);
    validValues = { label: 'Valid Refrigerant Types', values: refrigerantTypes };
  } else if (activityType === Scope1ActivityType.ProcessEmissions) {
    conditionalRequired.push(<li key='process'>Process Type</li>);
    validValues = { label: 'Valid Process Types', values: processTypes };
  }

  return (
    <Card className='tw:bg-muted/30 tw:mt-4'>
      <CardContent className='tw:p-4'>
        <div className='tw:flex tw:gap-3'>
          <Info className='tw:size-5 tw:text-blue-600 tw:shrink-0 tw:mt-2' />
          <div className='tw:w-full'>
            <h5 className='tw:font-semibold tw:mb-3'>Import Guidelines</h5>
            <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6'>
              <div>
                <h6 className='tw:font-semibold tw:text-sm tw:mb-2'>Required Fields</h6>
                <ul className='tw:text-sm tw:text-muted-foreground tw:space-y-1 tw:list-disc tw:list-inside tw:mb-0'>
                  {scope === EmissionScope.Scope1 && <li>Activity Type</li>}
                  <li>Period Start (YYYY-MM-DD)</li>
                  <li>Period End (YYYY-MM-DD)</li>
                  <li>Quantity (positive number)</li>
                  <li>Unit</li>
                  <li>Region (ISO country code e.g. GB, US)</li>
                  {conditionalRequired}
                  <li className='tw:text-muted-foreground/70'>Name / Label (optional)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
